// Reusable SEO component — sets title, meta, OG, canonical and JSON-LD per page.
import { Helmet } from "react-helmet-async";

const SITE_URL = "https://7med.ch";
const SITE_NAME = "7MED";
const DEFAULT_OG = "https://customer-assets.emergentagent.com/job_zealous-goodall-9/artifacts/3l0f3p4p_shutterstock_1850689243.jpg";

export default function SEO({
    title,
    description,
    path = "",
    ogImage,
    type = "website",
    keywords,
    noindex = false,
    structuredData,
    breadcrumbs,
}) {
    const url = `${SITE_URL}${path || "/"}`;
    const fullTitle = title?.endsWith("7MED") ? title : `${title} | 7MED`;
    const image = ogImage || DEFAULT_OG;

    const ldArray = [];
    if (structuredData) {
        Array.isArray(structuredData) ? ldArray.push(...structuredData) : ldArray.push(structuredData);
    }
    if (breadcrumbs?.length) {
        ldArray.push({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: breadcrumbs.map((b, i) => ({
                "@type": "ListItem",
                position: i + 1,
                name: b.name,
                item: `${SITE_URL}${b.path}`,
            })),
        });
    }

    return (
        <Helmet>
            <title>{fullTitle}</title>
            <meta name="description" content={description} />
            {keywords && <meta name="keywords" content={keywords} />}
            {noindex && <meta name="robots" content="noindex,nofollow" />}
            <link rel="canonical" href={url} />

            {/* OG */}
            <meta property="og:type" content={type} />
            <meta property="og:locale" content="it_CH" />
            <meta property="og:site_name" content={SITE_NAME} />
            <meta property="og:url" content={url} />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={description} />
            <meta property="og:image" content={image} />
            <meta property="og:image:alt" content={title} />

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={image} />

            {/* JSON-LD */}
            {ldArray.map((data, i) => {
                // Stable key: prefer @type if present, fall back to index
                const stableKey = data?.["@type"] ? `ld-${data["@type"]}-${i}` : `ld-${i}`;
                return (
                    <script key={stableKey} type="application/ld+json">{JSON.stringify(data)}</script>
                );
            })}
        </Helmet>
    );
}

// ===== Pre-built schema fragments =====

export const ORGANIZATION_SCHEMA = {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    "@id": `${SITE_URL}/#organization`,
    name: "7MED GROUP",
    alternateName: "7MED",
    url: SITE_URL,
    logo: "https://customer-assets.emergentagent.com/job_zealous-goodall-9/artifacts/o7ztlruv_7med.png",
    image: DEFAULT_OG,
    description: "Centro di formazione certificato SRC ed EduQua. Corsi BLS-AED, primo soccorso per aziende e patenti. Noleggio defibrillatori AED.",
    foundingDate: "2010",
    telephone: "+41912615199",
    email: "backoffice@7med.ch",
    areaServed: [{ "@type": "Country", name: "Switzerland" }, { "@type": "AdministrativeArea", name: "Ticino" }],
    sameAs: ["https://7med.ch"],
    knowsAbout: ["Primo Soccorso", "BLS-AED", "Rianimazione cardiopolmonare", "Defibrillatori AED", "Corsi Samaritani", "Carta Qualificazione Conducente", "OLL3 Art. 36"],
    hasCredential: [
        { "@type": "EducationalOccupationalCredential", credentialCategory: "Centro di formazione SRC certificato" },
        { "@type": "EducationalOccupationalCredential", credentialCategory: "Conforme OLL3 Art. 36" },
        { "@type": "EducationalOccupationalCredential", credentialCategory: "Docenti abilitati USTRA" },
    ],
};

export const LOCATIONS_SCHEMA = [
    { city: "Mendrisio", street: "Via Luigi Lavizzari 2", zip: "6850", lat: 45.8709, lng: 8.9821 },
    { city: "Savosa", street: "Via Cantonale 3", zip: "6942", lat: 46.0291, lng: 8.9472 },
    { city: "Bellinzona", street: "Via San Gottardo 22", zip: "6500", lat: 46.1944, lng: 9.0244 },
    { city: "Locarno", street: "Via ai Saleggi 5", zip: "6600", lat: 46.1712, lng: 8.7984 },
].map((s) => ({
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${SITE_URL}/#sede-${s.city.toLowerCase()}`,
    name: `7MED · ${s.city}`,
    parentOrganization: { "@id": `${SITE_URL}/#organization` },
    image: "https://customer-assets.emergentagent.com/job_zealous-goodall-9/artifacts/o7ztlruv_7med.png",
    telephone: "+41912615199",
    email: "backoffice@7med.ch",
    url: `${SITE_URL}/contatti`,
    address: {
        "@type": "PostalAddress",
        streetAddress: s.street,
        postalCode: s.zip,
        addressLocality: s.city,
        addressRegion: "TI",
        addressCountry: "CH",
    },
    geo: { "@type": "GeoCoordinates", latitude: s.lat, longitude: s.lng },
    openingHoursSpecification: [
        { "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], opens: "09:00", closes: "18:00" },
    ],
    priceRange: "CHF",
    areaServed: { "@type": "AdministrativeArea", name: "Ticino" },
}));

export function courseSchema(course) {
    return {
        "@context": "https://schema.org",
        "@type": "Course",
        "@id": `${SITE_URL}${course.href}`,
        name: course.fullTitle,
        description: course.tagline,
        provider: { "@id": `${SITE_URL}/#organization` },
        url: `${SITE_URL}${course.href}`,
        educationalCredentialAwarded: course.certification,
        timeRequired: course.duration,
        inLanguage: "it",
        availableLanguage: ["it", "de", "fr"],
        coursePrerequisites: "Nessuno",
        teaches: course.bullets,
        offers: course.price
            ? { "@type": "Offer", price: course.price.replace(/[^0-9.]/g, ""), priceCurrency: "CHF", availability: "https://schema.org/InStock", url: `${SITE_URL}${course.href}` }
            : { "@type": "Offer", priceSpecification: { "@type": "PriceSpecification", priceCurrency: "CHF", description: "Su preventivo" } },
        hasCourseInstance: {
            "@type": "CourseInstance",
            courseMode: "Onsite",
            inLanguage: "it",
            location: { "@type": "Place", address: { "@type": "PostalAddress", addressRegion: "Ticino", addressCountry: "CH" } },
        },
    };
}

export function blogPostSchema(post) {
    return {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: post.title,
        description: post.summary,
        image: post.cover_image || DEFAULT_OG,
        datePublished: post.published_at || post.created_at,
        dateModified: post.updated_at || post.published_at || post.created_at,
        author: { "@type": "Organization", name: post.author || "7MED", url: SITE_URL },
        publisher: { "@id": `${SITE_URL}/#organization` },
        mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_URL}/blog/${post.slug}` },
        articleSection: post.category,
        keywords: (post.tags || []).join(", "),
        inLanguage: "it",
    };
}

export function faqPageSchema(items) {
    return {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: items.map((it) => ({
            "@type": "Question",
            name: it.q,
            acceptedAnswer: { "@type": "Answer", text: it.a },
        })),
    };
}
